import { HeaderUserProfile } from '@/components/shared/HeaderUserProfile';
import { LanguageSwitcher } from '@/components/shared/LanguageSwitcher';
import { CSS_COLORS } from '@/constants/pageStyles';
import { usePermittedReports } from '@/features/landing/hooks/useReports';
import { cn } from '@/lib/utils';
import { ChevronRight } from 'lucide-react';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

interface PortalHeaderProps {
  className?: string;
  onLogoClick?: () => void;
}

export const PortalHeader = memo(function PortalHeader({
  className,
  onLogoClick,
}: PortalHeaderProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const reportId = searchParams.get('reportId');
  const moduleId = searchParams.get('moduleId');
  const pageId = searchParams.get('pageId');

  const { data: reportCategories } = usePermittedReports(moduleId || '');

  // Build home link - go back to landing page with moduleId
  const homeLink = useMemo(() => {
    if (moduleId) {
      return `/?moduleId=${moduleId}`;
    }
    return '/';
  }, [moduleId]);

  const breadcrumbData = useMemo(() => {
    if (!reportCategories || !reportId) return null;

    for (const category of reportCategories) {
      const report = category.reports?.find(
        (r: { id: string }) => r.id === reportId,
      );
      if (report) {
        const page = report.pages?.find((p: { id: string }) => p.id === pageId);
        return {
          categoryName: category.categoryName,
          reportName: report.reportName,
          pageName: page?.pageName || null,
        };
      }
    }
    return null;
  }, [reportCategories, reportId, pageId]);

  return (
    <header
      className={cn('flex-between bg-card h-[44px] pl-2 pr-4', className)}
      style={{ boxShadow: '0px 4px 20px 0px #83838340' }}
    >
      {/* Left Area: Breadcrumbs */}
      <div className="flex-start gap-2">
        {onLogoClick ? (
          <button
            type="button"
            onClick={onLogoClick}
            className="hover:text-primary flex items-center transition-colors"
            style={{ color: 'var(--custom-neutral-400)' }}
          >
            <span className="material-symbols-rounded" style={{ fontSize: 22 }}>menu</span>
          </button>
        ) : (
          <Link
            to={homeLink}
            className="hover:text-primary flex items-center transition-colors"
            style={{ color: 'var(--custom-neutral-400)' }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              className="h-[18px] w-[18px]"
            >
              <path
                d="M3 9.8125L12 3.1875L21 9.8125V20.8125H3V9.8125ZM5 10.9375V18.8125H19V10.9375L12 5.8125L5 10.9375ZM10 18.8125V12.8125H14V18.8125H10ZM17.5 8.3125L19.5 9.8125V5.3125H17.5V8.3125Z"
                fill="currentColor"
              />
            </svg>
          </Link>
        )}
        <ChevronRight
          size={18}
          style={{ color: 'var(--custom-neutral-400)' }}
        />
        {breadcrumbData ? (
          <>
            {/* Module / Category Name */}
            <span
              className="transition-colors"
              style={{
                color: 'var(--custom-neutral-400)',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {breadcrumbData.categoryName}
            </span>
            <ChevronRight
              size={18}
              style={{ color: 'var(--custom-neutral-400)' }}
            />
            {/* Report Name */}
            <span
              className="transition-colors"
              style={{
                color: breadcrumbData.pageName
                  ? 'var(--custom-neutral-400)'
                  : CSS_COLORS.primary,
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {breadcrumbData.reportName}
            </span>
            {/* Page Name - if exists */}
            {breadcrumbData.pageName && (
              <>
                <ChevronRight
                  size={18}
                  style={{ color: 'var(--custom-neutral-400)' }}
                />
                <span
                  className="transition-colors"
                  style={{
                    color: CSS_COLORS.primary,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {breadcrumbData.pageName}
                </span>
              </>
            )}
          </>
        ) : (
          <Link
            to={homeLink}
            className="hover:text-primary transition-colors"
            style={{
              color: 'var(--custom-neutral-400)',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            {t('landing.report.title')}
          </Link>
        )}
      </div>

      {/* Right Area: User Profile & Language Switcher */}
      <div className="flex shrink-0 items-center gap-2 md:gap-4">
        <LanguageSwitcher />
        <div className="bg-border h-8 w-px" />
        <HeaderUserProfile avatarSize={36} />
      </div>
    </header>
  );
});
